'use client';

import { useEffect, useState } from 'react';
import { Box, IconButton, Menu, MenuItem } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useAtom } from 'jotai';
import { signIn, useSession } from 'next-auth/react';
import { FormControlLabel, Tooltip } from '@/primitives';
import { FilterIcon } from '@/components/icons';
import MyHerdLinkingInfoDialog from '@/components/publicSearch/MyHerdLinkingInfoDialog';
import SaveFilterButton from '@/components/publicSearch/searchSection/SaveFilterButton';
import { AUTH_PROVIDER, SESSION_STATUS } from '@/constants/auth';
import { RESULT_DISPLAY_TYPE } from '@/constants/resultDisplay';
import { SEARCH_TYPE } from '@/constants/searchType';
import { useAnimalDisplayType } from '@/hooks/publicSearch/animal/useAnimalDisplayType';
import { useMemberAssociation } from '@/publicSearch/graphql/memberAssociation/hooks';
import useFilterStore from '@/publicSearch/store/useFilterStore';
import useGridStore from '@/publicSearch/store/useGridStore';
import useSearchStore from '@/publicSearch/store/useSearchStore';
import { supportsSavedFilters } from '@/publicSearch/store/useSavedFiltersStore';
import { useSearchType } from '@/hooks/publicSearch/useSearchType';
import type { SearchType } from '@/hooks/publicSearch/useSearchType';
import { useShareSearch } from '@/hooks/publicSearch/useShareSearch';
import { ACTIVE_SALE_LISTINGS_TOGGLE_LABEL } from '../../constants';
import { openAdvanceFilterDialogAtom } from './AdvancedFilterDialog';
import QuickSearchTipsInfoDialog from './QuickSearchTipsInfoDialog';
import { shouldShowQuickSearchTips } from '../quickSearchCopy';
import { useCallbackUrl } from '../hooks/useCallbackUrl';
import { StyledSearchIconButton } from '../../searchPage/SearchActions';
import CompactSearchSwitch from './CompactSearchSwitch';
import {
  searchControlIconButtonSx,
  searchControlMenuDivider,
  searchControlMenuItemSx,
  searchControlToggleListSx,
} from './searchControlStyles';

const basicFilterTabs: SearchType[] = [
  SEARCH_TYPE.ANIMAL,
  SEARCH_TYPE.MEMBERS,
  SEARCH_TYPE.SALE_CATALOGUES_DETAIL_ANIMALS,
  SEARCH_TYPE.SEMEN_CATALOGUES_DETAIL_ANIMALS,
];

export default function SearchFilterControl() {
  const searchType = useSearchType();
  const { status } = useSession();
  const callbackUrl = useCallbackUrl();
  const isAuthenticated = status === SESSION_STATUS.AUTHENTICATED;
  const { data: memberAssocData } = useMemberAssociation(!isAuthenticated);
  const hasMemberAssociations =
    (memberAssocData?.memberAssociation?.length ?? 0) > 0;
  const { resultDisplay } = useAnimalDisplayType();
  const isCardView = resultDisplay === RESULT_DISPLAY_TYPE.GALLERY;
  const { filters, filterGroups, clearFilter } = useFilterStore(searchType);
  const {
    animalsOnSaleOnly,
    gridSortModel,
    myAnimals,
    paginationModel,
    setAnimalsOnSaleOnly,
    setMyAnimals,
    setPaginationModel,
  } = useGridStore();
  const { basicSearchInput, isAiMode, setOpenBasicSearchDialog } =
    useSearchStore();
  const [, setOpenAdvancedFilter] = useAtom(openAdvanceFilterDialogAtom);
  const {
    shareSearch,
    isSharing,
    hasActiveFilters: hasShareableFilters,
  } = useShareSearch();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [saveFilterOpen, setSaveFilterOpen] = useState<boolean>(false);
  const [myHerdInfoOpen, setMyHerdInfoOpen] = useState<boolean>(false);
  const [searchTipsOpen, setSearchTipsOpen] = useState<boolean>(false);
  const [buttonElement, setButtonElement] = useState<HTMLButtonElement | null>(
    null,
  );

  const open = Boolean(anchorEl);
  const showMyHerd = searchType === SEARCH_TYPE.ANIMAL;
  const showBasicAdvanced = basicFilterTabs.includes(searchType);
  const showSearchTips = shouldShowQuickSearchTips(searchType) && !isAiMode;
  const showControl =
    searchType !== SEARCH_TYPE.SALE_CATALOGUES &&
    ((supportsSavedFilters(searchType) && !isCardView) ||
      showBasicAdvanced ||
      showMyHerd);
  const myHerdInteractive = isAuthenticated && hasMemberAssociations;
  const hasGroupedFilters = filterGroups.some(
    (group) => group.filters.length > 0,
  );
  // SALE_CATALOGUES is excluded by `showControl` below, so saved-filter state
  // only ever covers the non-catalogue search types.
  const hasFiltersToSave =
    filters.length > 0 ||
    basicSearchInput.trim() !== '' ||
    hasGroupedFilters ||
    gridSortModel.length > 0;
  const closeMenu = () => setAnchorEl(null);

  useEffect(() => {
    if (status === SESSION_STATUS.UNAUTHENTICATED) setMyAnimals(false);
  }, [setMyAnimals, status]);

  if (!showControl) return null;

  const signInForMyHerd = () => {
    closeMenu();
    void signIn(AUTH_PROVIDER, { callbackUrl });
  };

  return (
    <>
      <Tooltip title="Filter" placement="bottom">
        <StyledSearchIconButton
          ref={setButtonElement}
          sx={searchControlIconButtonSx(open)}
          onClick={(event) => setAnchorEl(event.currentTarget)}
          aria-label="Filter"
          data-dd-action-name="Filter: Open Menu"
        >
          <FilterIcon color="primary" sx={{ fontSize: 20 }} />
        </StyledSearchIconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        open={open}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        onClose={closeMenu}
        slotProps={{
          list:
            showMyHerd || showSearchTips
              ? { sx: searchControlToggleListSx }
              : undefined,
        }}
      >
        {showBasicAdvanced && [
          <MenuItem
            key="filter-basic-search"
            data-dd-action-name="Filter: Basic Search"
            onClick={() => {
              closeMenu();
              setOpenAdvancedFilter(false);
              setOpenBasicSearchDialog(true);
            }}
            sx={{
              ...searchControlMenuItemSx,
              borderBottom: searchControlMenuDivider,
            }}
          >
            Basic Search
          </MenuItem>,
          <MenuItem
            key="filter-advanced-search"
            data-dd-action-name="Filter: Advanced Search"
            onClick={() => {
              closeMenu();
              setOpenBasicSearchDialog(false);
              setOpenAdvancedFilter(true);
            }}
            sx={{
              ...searchControlMenuItemSx,
              borderBottom: searchControlMenuDivider,
            }}
          >
            Advanced Search
          </MenuItem>,
        ]}
        {showMyHerd && [
          <Tooltip
            key="filter-my-herd"
            title={!isAuthenticated ? 'Sign in to filter by My Herd.' : ''}
            placement="left"
            disableHoverListener={isAuthenticated}
          >
            <MenuItem
              onClick={(event) => {
                if (!isAuthenticated) {
                  signInForMyHerd();
                  return;
                }
                event.stopPropagation();
              }}
              onKeyDown={(event) => {
                if (!isAuthenticated) {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    signInForMyHerd();
                  }
                  return;
                }
                event.stopPropagation();
              }}
              sx={{
                py: 0.5,
                borderBottom: searchControlMenuDivider,
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  label: { gap: '4px', m: 0 },
                  '.MuiSwitch-root': { order: 1 },
                }}
              >
                <FormControlLabel
                  control={
                    <CompactSearchSwitch
                      data-dd-action-name="Filter: My Herd Toggle"
                      checked={myHerdInteractive ? myAnimals : false}
                      disabled={!myHerdInteractive}
                      onChange={(event) => {
                        if (!myHerdInteractive) return;
                        setMyAnimals(event.target.checked);
                        setPaginationModel({ ...paginationModel, page: 0 });
                      }}
                    />
                  }
                  label={
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                      }}
                    >
                      {!isAuthenticated && (
                        <LockOutlinedIcon
                          aria-hidden
                          sx={{ fontSize: 16, color: 'text.secondary' }}
                        />
                      )}
                      {isAuthenticated && !hasMemberAssociations && (
                        <Tooltip title="How to link My Herd" placement="top">
                          <IconButton
                            size="small"
                            aria-label="How to link My Herd"
                            data-dd-action-name="Filter: My Herd Linking Info"
                            onClick={(event) => {
                              event.stopPropagation();
                              event.preventDefault();
                              closeMenu();
                              setMyHerdInfoOpen(true);
                            }}
                            onMouseDown={(event) => event.stopPropagation()}
                            sx={{ p: 0.25, color: 'text.secondary' }}
                          >
                            <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                      My Herd
                    </Box>
                  }
                />
              </Box>
            </MenuItem>
          </Tooltip>,
          <MenuItem
            key="filter-active-sale-listings"
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => event.stopPropagation()}
            sx={{
              py: 0.5,
              borderBottom:
                showBasicAdvanced ||
                (supportsSavedFilters(searchType) && !isCardView)
                  ? searchControlMenuDivider
                  : undefined,
            }}
          >
            <Box
              sx={{
                width: '100%',
                label: { gap: '4px', m: 0 },
                '.MuiSwitch-root': { order: 1 },
              }}
            >
              <Tooltip
                title="Show only animals with an active sale or private treaty listing."
                placement="left"
              >
                <FormControlLabel
                  control={
                    <CompactSearchSwitch
                      data-dd-action-name="Filter: Active Sale Listings Toggle"
                      checked={animalsOnSaleOnly}
                      onChange={(event) => {
                        setAnimalsOnSaleOnly(event.target.checked);
                        setPaginationModel({ ...paginationModel, page: 0 });
                      }}
                    />
                  }
                  label={
                    <Box
                      component="span"
                      sx={{ fontSize: '12px', whiteSpace: 'nowrap' }}
                    >
                      {ACTIVE_SALE_LISTINGS_TOGGLE_LABEL}
                    </Box>
                  }
                />
              </Tooltip>
            </Box>
          </MenuItem>,
        ]}
        {supportsSavedFilters(searchType) &&
          !isCardView && [
            <MenuItem
              key="filter-save-search"
              data-dd-action-name="Filter: Save Search"
              disabled={!hasFiltersToSave}
              onClick={() => {
                closeMenu();
                setSaveFilterOpen(true);
              }}
              sx={{
                height: '36px',
                minHeight: '36px',
                py: 0,
                paddingLeft: '8px !important',
                fontSize: '12px',
                borderBottom: searchControlMenuDivider,
              }}
            >
              Save search
            </MenuItem>,
            <MenuItem
              key="filter-share-link"
              data-dd-action-name="Filter: Share Link"
              disabled={!hasShareableFilters || isSharing}
              onClick={() => {
                closeMenu();
                shareSearch();
              }}
              sx={{
                height: '36px',
                minHeight: '36px',
                py: 0,
                paddingLeft: '8px !important',
                fontSize: '12px',
                borderBottom: searchControlMenuDivider,
              }}
            >
              Share link
            </MenuItem>,
            <MenuItem
              key="filter-clear-filter"
              data-dd-action-name="Filter: Clear"
              onClick={() => {
                closeMenu();
                clearFilter();
              }}
              sx={searchControlMenuItemSx}
            >
              Clear filter
            </MenuItem>,
          ]}
        {showSearchTips && (
          <MenuItem
            data-dd-action-name="Filter: Search Tips"
            aria-label="Search tips. Opens explanation dialog."
            onClick={() => {
              closeMenu();
              setSearchTipsOpen(true);
            }}
            sx={{
              py: 0.5,
              borderTop: searchControlMenuDivider,
            }}
          >
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                pl: '8px',
                height: '36px',
                width: '100%',
                fontSize: '12px',
              }}
            >
              <InfoOutlinedIcon
                aria-hidden
                sx={{ fontSize: 16, color: 'text.secondary' }}
              />
              Search tips
            </Box>
          </MenuItem>
        )}
      </Menu>
      {supportsSavedFilters(searchType) && !isCardView && (
        <SaveFilterButton
          open={saveFilterOpen}
          onClose={() => setSaveFilterOpen(false)}
          anchorEl={buttonElement}
        />
      )}
      <MyHerdLinkingInfoDialog
        open={myHerdInfoOpen}
        onClose={() => setMyHerdInfoOpen(false)}
      />
      <QuickSearchTipsInfoDialog
        open={searchTipsOpen}
        onClose={() => setSearchTipsOpen(false)}
        searchType={searchType}
      />
    </>
  );
}
